// components/system/RefreshHistoryTable.jsx
import React from 'react';
import { Clock } from 'lucide-react';
import { formatTimestamp, getRefreshStatusIcon } from '../../utils/systemUtils';

// Component constants
const COMPLETION_THRESHOLDS = {
    PARTIAL: 0.9, // 90% completion threshold for partial status
    FULL: 1.0     // 100% completion for full status
};

const BADGE_THRESHOLDS = {
    HIGH: 90,     // Green badge threshold (90%+)
    MEDIUM: 50    // Yellow badge threshold (50-89%)
};

const RefreshHistoryTable = ({ 
    refreshHistory = [], 
    maxRows = 5,
    isRefreshActive = false,
    currentPhase = null,
    title = "Recent Refresh History"
}) => {
    // Filter out redundant full_refresh entries and limit results
    const filteredHistory = refreshHistory
        .filter(item => item.component !== 'full_refresh')
        .slice(0, maxRows);

    const getStatusDisplay = (item) => {
        const completion = Math.min(item.completeness || 0, 1); // Cap at 100%
        
        // During active refresh, show more accurate status
        if (isRefreshActive) {
            if (item.status === 'IN_PROGRESS') {
                return { icon: '🔄', label: 'ACTIVE', color: 'blue' };
            }
        }
        
        // Historical status logic
        if (item.status === 'IN_PROGRESS' && completion === COMPLETION_THRESHOLDS.FULL) {
            return { icon: '⏳', label: 'FINISHING', color: 'blue' };
        }
        if (item.status === 'COMPLETE' && completion >= COMPLETION_THRESHOLDS.PARTIAL) {
            return { icon: '✅', label: 'COMPLETE', color: 'green' };
        }
        if (item.status === 'COMPLETE' && completion < COMPLETION_THRESHOLDS.PARTIAL) {
            return { icon: '⚠️', label: 'PARTIAL', color: 'orange' };
        }
        return getRefreshStatusIcon(item.status);
    };

    const getCompletionBadge = (completeness) => {
        if (completeness === undefined) return null;
        
        const percentage = Math.min(completeness * 100, 100);
        
        let badgeClass = 'text-xs px-2 py-1 rounded ';
        if (percentage >= BADGE_THRESHOLDS.HIGH) {
            badgeClass += 'bg-green-100 text-green-700';
        } else if (percentage >= BADGE_THRESHOLDS.MEDIUM) {
            badgeClass += 'bg-yellow-100 text-yellow-700';
        } else {
            badgeClass += 'bg-red-100 text-red-700';
        }
        
        return (
            <span className={badgeClass}>
                {percentage.toFixed(1)}%
            </span>
        );
    };

    if (!filteredHistory || filteredHistory.length === 0) {
        return (
            <div className="bg-white border rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <span>{title}</span>
                </h3>
                
                <div className="text-center py-8 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No refresh history available</p>
                    <p className="text-sm">Refresh operations will appear here</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <span>{title}</span>
            </h3>
            
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b">
                            <th className="text-left py-2">Component</th>
                            <th className="text-left py-2">Status</th>
                            <th className="text-left py-2">Completed</th>
                            <th className="text-left py-2">Progress</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredHistory.map((item, index) => {
                            const displayStatus = getStatusDisplay(item);
                            const completedTime = item.status === 'COMPLETE' && item.last_success 
                                ? item.last_success 
                                : item.last_attempt;
                            
                            return (
                                <tr key={index} className="border-b">
                                    <td className="py-2 font-medium">{item.component}</td>
                                    <td className="py-2">
                                        <span className="flex items-center space-x-2">
                                            <span>{displayStatus.icon}</span>
                                            <span className={`text-${displayStatus.color || 'gray'}-600`}>
                                                {displayStatus.label || item.status}
                                            </span>
                                        </span>
                                    </td>
                                    <td className="py-2">
                                        {formatTimestamp(completedTime) || 'In Progress'}
                                    </td>
                                    <td className="py-2">
                                        {getCompletionBadge(item.completeness)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            
            {refreshHistory && refreshHistory.length > maxRows && (
                <div className="mt-3 text-center">
                    <span className="text-xs text-gray-500">
                        Showing {maxRows} of {refreshHistory.length} recent operations
                    </span>
                </div>
            )}
        </div>
    );
};

export default RefreshHistoryTable;